// Widgets with styles: one library entry each, drawn in the style the trader picks in the widget's
// settings (the first style is the default). Net P&L, win rate, profit factor, equity & drawdown,
// streaks, account progress, daily loss left, consistency rule, payout tracker, needs review and
// P&L by time of day.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountStatus } from './balance';
import { isPropAccount } from './DashboardOverview';
import InfoTip from './InfoTip';
import { currentTradingDay, netPnl, tradingDayKey, usd } from './pnl';
import { unjournaledTrades } from './stats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { NEW_STYLES, NewWidget } from './NewWidgets';

// Each styled widget's styles, in order; the first is the default.
export const STYLES = {
  net: [['a', 'Line'], ['b', 'Daily bars'], ['c', 'Split']],
  winRate: [['a', 'Ring'], ['b', 'Bar'], ['c', 'Compare']],
  profitFactor: [['c', 'Compare'], ['a', 'Gauge'], ['b', 'Scale']],
  equity: [['a', 'Shaded'], ['b', 'Two charts']],
  streak: [['a', 'Squares'], ['b', 'Ring'], ['c', 'Dots']],
  propProgress: [['a', 'One bar'], ['b', 'Two bars'], ['c', 'Zones']],
  dailyLoss: [['a', 'Bar'], ['b', 'Gauge'], ['c', 'Blocks']],
  consistency: [['a', 'Bar'], ['b', 'Ring'], ['c', 'Compare']],
  payouts: [['c', 'Steps'], ['a', 'Days']],
  needsReview: [['a', 'Progress'], ['c', 'List']],
  byHour: [['a', 'Columns'], ['b', 'Rows'], ['c', 'Heat']],
  ...NEW_STYLES,
};
export const styleOf = (w) => {
  const list = STYLES[w.id];
  return list && list.some(([id]) => id === w.style) ? w.style : list ? list[0][0] : null;
};

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const money = (n) => `${n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const shortMoney = (n) => (Math.abs(n) >= 1000 ? `${n > 0 ? '+' : n < 0 ? '−' : ''}$${(Math.abs(n) / 1000).toFixed(1)}K` : signed(n).replace('.00', ''));
const dayShort = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? 'AM' : 'PM'}`;
const NY_HOUR = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' });
const C = 2 * Math.PI * 34;

function Card({ title, info, extra, children, className = '' }) {
  const { t } = useI18n();
  return (
    <section className={`sw ${className}`}>
      <div className="sw-head"><span className="sw-title">{t(title)}{info && <InfoTip label={t(title)} text={t(info)} />}</span>{extra}</div>
      {children}
    </section>
  );
}
function Chips({ items }) {
  return <span className="sw-chips">{items.filter(Boolean).map(([text, kind]) => <span key={text} className={`sw-chip ${kind || ''}`}>{text}</span>)}</span>;
}
function Seg({ options, value, onChange }) {
  const { t } = useI18n();
  return (
    <span className="sw-seg" role="group">
      {options.map(([id, label]) => <button key={id} type="button" className={value === id ? 'on' : ''} aria-pressed={value === id} onClick={() => onChange(id)}>{t(label)}</button>)}
    </span>
  );
}
function Ring({ pct, kind, label, sub }) {
  return (
    <svg className="sw-ring" viewBox="0 0 84 84" aria-hidden="true">
      <circle cx="42" cy="42" r="34" className="tr" />
      <circle cx="42" cy="42" r="34" className={`on ${kind}`} strokeDasharray={`${Math.max(0, Math.min(1, pct / 100)) * C} ${C}`} transform="rotate(-90 42 42)" />
      <text x="42" y={sub ? 45 : 48}>{label}</text>
      {sub && <text x="42" y="57" className="s">{sub}</text>}
    </svg>
  );
}

// --- Shared numbers ----------------------------------------------------------------------------

function stats(trades) {
  const nets = trades.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0).map((n) => -n);
  const gw = wins.reduce((s, n) => s + n, 0);
  const gl = losses.reduce((s, n) => s + n, 0);
  const avgW = wins.length ? gw / wins.length : 0;
  const avgL = losses.length ? gl / losses.length : 0;
  const ratio = avgL ? avgW / avgL : null;
  return {
    net: gw - gl, count: trades.length, wins: wins.length, losses: losses.length, gw, gl, avgW, avgL, ratio,
    winRate: trades.length ? (wins.length / trades.length) * 100 : null,
    breakEven: ratio ? 100 / (1 + ratio) : null,
    pf: gl ? gw / gl : gw ? Infinity : null,
    fees: trades.reduce((s, x) => s + (x.fees || 0), 0),
  };
}
function days(trades) {
  const map = new Map();
  for (const x of [...trades].sort((a, b) => new Date(a.date) - new Date(b.date))) {
    const k = tradingDayKey(x.date);
    map.set(k, (map.get(k) || 0) + netPnl(x));
  }
  return [...map.entries()].map(([key, net]) => ({ key, net }));
}
function since(trades, period) {
  const today = currentTradingDay();
  if (period === 'all') return trades;
  if (period === 'today') return trades.filter((x) => tradingDayKey(x.date) === today);
  if (period === 'month') return trades.filter((x) => tradingDayKey(x.date).slice(0, 7) === today.slice(0, 7));
  const from = new Date(`${today}T12:00:00Z`);
  if (period === 'week') from.setUTCDate(from.getUTCDate() - ((from.getUTCDay() + 6) % 7));
  else from.setUTCDate(from.getUTCDate() - (period === '90d' ? 89 : 29));
  const key = from.toISOString().slice(0, 10);
  return trades.filter((x) => tradingDayKey(x.date) >= key);
}
function linePath(values, w, h, pad = 6, lo = Math.min(0, ...values), hi = Math.max(0, ...values)) {
  const span = hi - lo || 1;
  const pts = values.map((v, i) => [(i / Math.max(1, values.length - 1)) * w, pad + (1 - (v - lo) / span) * (h - 2 * pad)]);
  return { d: pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' '), pts, y0: pad + (1 - (0 - lo) / span) * (h - 2 * pad) };
}
function Empty({ text }) {
  const { t } = useI18n();
  return <p className="sw-empty">{t(text)}</p>;
}

// --- 1. Net P&L --------------------------------------------------------------------------------

export function NetPnl({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('all');
  const list = since(trades, period);
  const s = stats(list);
  const d = days(list);
  const running = d.reduce((acc, x) => [...acc, (acc[acc.length - 1] || 0) + x.net], []);
  const periods = [['today', 'Today'], ['week', 'Week'], ['month', 'Month'], ['all', 'All']];
  const seg = <Seg options={style === 'b' ? periods.slice(1) : periods} value={period} onChange={setPeriod} />;
  if (style === 'b') {
    const max = Math.max(1, ...d.map((x) => Math.abs(x.net)));
    const best = d.length ? Math.max(...d.map((x) => x.net)) : 0;
    const worst = d.length ? Math.min(...d.map((x) => x.net)) : 0;
    return (
      <Card title="Net P&L" info="Everything these trades made or lost, after commissions and fees." extra={seg}>
        <div className="sw-row between"><div><div className={`sw-big ${tone(s.net)}`}>{signed(s.net)}</div><span className="sw-muted">{t(s.count === 1 ? '{n} trade' : '{n} trades', { n: s.count })}</span></div>
          {d.length > 0 && <div className="sw-minicol"><small>{t('Best day')}</small><b className="gain">{signed(best)}</b><small>{t('Worst day')}</small><b className="loss">{signed(worst)}</b></div>}</div>
        {d.length ? <div className="sw-dbars">{d.slice(-30).map((x) => <i key={x.key} className={tone(x.net)} style={{ height: `${(Math.abs(x.net) / max) * 100}%` }} title={`${dayShort(x.key)} ${signed(x.net)}`} />)}</div> : <Empty text="No trades in this period." />}
        <span className="sw-foot">{t('{g} green days · {r} red days', { g: d.filter((x) => x.net > 0).length, r: d.filter((x) => x.net < 0).length })}</span>
      </Card>
    );
  }
  const line = running.length > 1 ? linePath(running, 300, 64) : null;
  if (style === 'c') {
    const week = stats(since(trades, 'week')).net;
    const today = stats(since(trades, 'today')).net;
    return (
      <Card title="Net P&L" info="Everything these trades made or lost, after commissions and fees." extra={seg}>
        <div className="sw-split"><div><div className={`sw-big ${tone(s.net)}`}>{signed(s.net)}</div><span className="sw-muted">{t('after {fees} in fees', { fees: money(s.fees) })}</span></div>
          {line && <svg className="sw-spark" viewBox="0 0 300 64" preserveAspectRatio="none"><path d={line.d} className={`sw-ln ${s.net >= 0 ? 'gain' : 'loss'}`} /></svg>}</div>
        <div className="sw-tri"><span><small>{t('Today')}</small><b className={tone(today)}>{signed(today)}</b></span><span><small>{t('This week')}</small><b className={tone(week)}>{signed(week)}</b></span><span><small>{t('Per trade')}</small><b className={tone(s.net)}>{s.count ? signed(s.net / s.count) : '—'}</b></span></div>
      </Card>
    );
  }
  return (
    <Card title="Net P&L · after fees" info="Everything these trades made or lost, after commissions and fees." extra={seg}>
      <div className={`sw-big ${tone(s.net)}`}>{signed(s.net)}</div>
      <Chips items={[[t('{n} W', { n: s.wins }), 'g'], [t('{n} L', { n: s.losses }), 'r'], s.winRate !== null && [t('{p}% win rate', { p: Math.round(s.winRate) })]]} />
      {line ? (
        <svg className="sw-spark tall" viewBox="0 0 300 64" preserveAspectRatio="none">
          <defs><linearGradient id="sw-net" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={s.net >= 0 ? '#2fe39a' : '#ff5d73'} stopOpacity=".32" /><stop offset="1" stopColor={s.net >= 0 ? '#2fe39a' : '#ff5d73'} stopOpacity="0" /></linearGradient></defs>
          <line x1="0" x2="300" y1={line.y0} y2={line.y0} className="sw-zero" />
          <path d={`${line.d} L300 64 L0 64 Z`} fill="url(#sw-net)" />
          <path d={line.d} className={`sw-ln ${s.net >= 0 ? 'gain' : 'loss'}`} />
        </svg>
      ) : <Empty text="No trades in this period." />}
      <span className="sw-foot">{t('Fees already taken out: this is what you kept.')}</span>
    </Card>
  );
}

// --- 2. Win rate -------------------------------------------------------------------------------

export function WinRate({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('all');
  const s = stats(since(trades, period));
  const seg = <Seg options={[['30d', '30D'], ['all', 'All']]} value={period} onChange={setPeriod} />;
  const info = 'The share of your trades that made money, next to the win rate you need to break even at your average win and loss size.';
  if (s.winRate === null) return <Card title="Win rate" info={info} extra={seg}><Empty text="No trades in this period." /></Card>;
  const wr = Math.round(s.winRate);
  const be = s.breakEven === null ? null : Math.round(s.breakEven);
  const above = be === null || s.winRate >= s.breakEven;
  if (style === 'b') {
    return (
      <Card title="Win rate" info={info} extra={seg}>
        <div className="sw-big">{wr}%</div>
        <div className="sw-wl"><b style={{ flex: s.wins || 0.01 }} /><i style={{ flex: s.losses || 0.01 }} />{be !== null && <span className="sw-mark" style={{ left: `${be}%` }}><em>{t('break-even {p}%', { p: be })}</em></span>}</div>
        <div className="sw-row between sw-muted"><span>{t('{n} wins', { n: s.wins })}</span><span>{t('{n} losses', { n: s.losses })}</span></div>
        {be !== null && <span className="sw-foot">{above ? t('{n} points above what your win size needs.', { n: wr - be }) : t('{n} points below what your win size needs.', { n: be - wr })}</span>}
      </Card>
    );
  }
  if (style === 'c') {
    return (
      <Card title="Win rate" info={info} extra={seg}>
        <div className="sw-cmp">
          <span><small>{t('Your win rate')}</small><b className={above ? 'gain' : 'loss'}>{wr}%</b><span className="sw-bar"><i className={above ? 'g' : 'r'} style={{ width: `${wr}%` }} /></span></span>
          {be !== null && <span><small>{t('Needed to break even')}</small><b>{be}%</b><span className="sw-bar"><i className="m" style={{ width: `${be}%` }} /></span></span>}
        </div>
        {s.ratio !== null && <span className="sw-foot">{t('Your average win is {x}× your average loss.', { x: s.ratio.toFixed(2) })}</span>}
      </Card>
    );
  }
  return (
    <Card title="Win rate" info={info} extra={seg}>
      <div className="sw-row nowrap gap"><Ring pct={s.winRate} kind={above ? 'g' : 'r'} label={`${wr}%`} />
        <div className="sw-stack"><span className="sw-kv"><span>{t('Wins')}</span><b>{s.wins}</b></span><span className="sw-kv"><span>{t('Losses')}</span><b>{s.losses}</b></span>{be !== null && <Chips items={[[t(above ? 'Above break-even' : 'Below break-even'), above ? 'g' : 'r']]} />}</div></div>
      {be !== null && <span className="sw-foot">{t('You need {p}% to break even at your {x}× payoff.', { p: be, x: s.ratio.toFixed(2) })}</span>}
    </Card>
  );
}

// --- 3. Profit factor --------------------------------------------------------------------------

export function ProfitFactor({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('all');
  const s = stats(since(trades, period));
  const seg = <Seg options={[['30d', '30D'], ['all', 'All']]} value={period} onChange={setPeriod} />;
  const info = 'What your winners made divided by what your losers lost. Above 1.00 you make money; 1.50 or more is strong.';
  if (s.pf === null) return <Card title="Profit factor" info={info} extra={seg}><Empty text="Needs a win and a loss." /></Card>;
  const pfText = s.pf === Infinity ? '∞' : s.pf.toFixed(2);
  const good = s.pf >= 1;
  const capped = Math.min(s.pf === Infinity ? 2.5 : s.pf, 2.5);
  if (style === 'a') {
    const ang = (capped / 2.5) * 180;
    const nx = 70 + 50 * Math.cos(((180 - ang) * Math.PI) / 180);
    const ny = 66 - 50 * Math.sin(((180 - ang) * Math.PI) / 180);
    return (
      <Card title="Profit factor" info={info} extra={seg}>
        <svg className="sw-gauge" viewBox="0 0 140 76" aria-hidden="true">
          <path d="M18 66 A52 52 0 0 1 49.5 18.2" className="gz r" /><path d="M50.5 17.8 A52 52 0 0 1 89.5 17.8" className="gz y" /><path d="M90.5 18.2 A52 52 0 0 1 122 66" className="gz g" />
          <line x1="70" y1="66" x2={nx} y2={ny} className="needle" /><circle cx="70" cy="66" r="4" className="hub" />
        </svg>
        <div className="sw-center"><div className={`sw-big ${good ? 'gain' : 'loss'}`}>{pfText}</div><Chips items={[[t(good ? 'Above 1.00 · making money' : 'Below 1.00 · losing money'), good ? 'g' : 'r']]} /></div>
      </Card>
    );
  }
  if (style === 'b') {
    return (
      <Card title="Profit factor" info={info} extra={seg}>
        <div className={`sw-big ${good ? 'gain' : 'loss'}`}>{pfText}</div>
        <div className="sw-scale"><span className="z r" /><span className="z y" /><span className="z g" /><span className="sw-pin" style={{ left: `${(capped / 2.5) * 100}%` }} /></div>
        <div className="sw-row between sw-tiny"><span>0</span><span>{t('1.0 break-even')}</span><span>{t('1.5 strong')}</span><span>2.5+</span></div>
        <span className="sw-foot">{t('Gross wins {w} ÷ gross losses {l}.', { w: money(s.gw), l: money(s.gl) })}</span>
      </Card>
    );
  }
  return (
    <Card title="Profit factor" info={info} extra={seg}>
      <div className="sw-row between nowrap"><div className={`sw-big ${good ? 'gain' : 'loss'}`}>{pfText}</div><span className="sw-muted sw-right">{t('For every $1 lost, you make back')} <b>${s.pf === Infinity ? '∞' : s.pf.toFixed(2)}</b></span></div>
      <div className="sw-cmp">
        <span><small>{t('Gross wins')}</small><b className="gain">{money(s.gw)}</b><span className="sw-bar"><i className="g" style={{ width: `${(s.gw / Math.max(s.gw, s.gl)) * 100}%` }} /></span></span>
        <span><small>{t('Gross losses')}</small><b className="loss">{money(-s.gl)}</b><span className="sw-bar"><i className="r" style={{ width: `${(s.gl / Math.max(s.gw, s.gl)) * 100}%` }} /></span></span>
      </div>
    </Card>
  );
}

// --- 4. Equity curve & drawdown ----------------------------------------------------------------

export function EquityDrawdown({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('all');
  const d = days(since(trades, period));
  const seg = <Seg options={[['30d', '30D'], ['90d', '90D'], ['all', 'All']]} value={period} onChange={setPeriod} />;
  const info = 'Your running P&L day by day. The red shading shows how far you were below your best point; the deepest red is your max drawdown.';
  if (d.length < 2) return <Card title="Equity curve & drawdown" info={info} extra={seg}><Empty text="The chart fills in after a few trading days." /></Card>;
  let run = 0;
  let peak = 0;
  const equity = [];
  const peaks = [];
  const dd = [];
  for (const x of d) {
    run += x.net;
    peak = Math.max(peak, run);
    equity.push(run);
    peaks.push(peak);
    dd.push(run - peak);
  }
  const maxDD = Math.min(...dd);
  const nowDD = dd[dd.length - 1];
  const green = d.filter((x) => x.net > 0).length;
  const red = d.filter((x) => x.net < 0).length;
  const axis = <div className="sw-axis"><span>{dayShort(d[0].key)}</span><span>{dayShort(d[Math.floor(d.length / 2)].key)}</span><span>{dayShort(d[d.length - 1].key)}</span></div>;
  const lo = Math.min(0, ...equity);
  const hi = Math.max(0, ...equity);
  if (style === 'b') {
    const top = linePath(equity, 560, 110, 8);
    const under = linePath(dd, 560, 56, 4, Math.min(...dd, -1), 0);
    return (
      <Card title="Equity curve & drawdown" info={info} extra={seg}>
        <div className="sw-row between"><span className="sw-muted">{t('Running P&L')}</span><b className={tone(run)}>{signed(run)}</b></div>
        <svg className="sw-chart sm" viewBox="0 0 560 110" preserveAspectRatio="none"><line x1="0" x2="560" y1={top.y0} y2={top.y0} className="sw-zero" /><path d={top.d} className="sw-ln cyan" /></svg>
        <div className="sw-row between"><span className="sw-muted">{t('Below your best point')}</span><b className="loss">{t('{now} now · {worst} worst', { now: money(nowDD), worst: money(maxDD) })}</b></div>
        <svg className="sw-chart xs" viewBox="0 0 560 56" preserveAspectRatio="none"><path d={`M0 ${under.y0} ${under.d.slice(1)} L560 ${under.y0} Z`} className="sw-under" /></svg>
        {axis}
      </Card>
    );
  }
  const e = linePath(equity, 560, 150, 8, lo, hi);
  const p = linePath(peaks, 560, 150, 8, lo, hi);
  const shade = `${p.d} ${e.pts.slice().reverse().map(([x, y]) => `L${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')} Z`;
  return (
    <Card title="Equity curve & drawdown" info={info} extra={seg}>
      <div className="sw-row between"><div className={`sw-big ${tone(run)}`}>{signed(run)}</div>
        <Chips items={[[t('Max drawdown {n}', { n: money(maxDD) }), 'r'], [nowDD < 0 ? t('{n} from your high', { n: money(nowDD) }) : t('At your high'), nowDD < 0 ? '' : 'g'], [t('{g} green · {r} red', { g: green, r: red })]]} /></div>
      <svg className="sw-chart" viewBox="0 0 560 150" preserveAspectRatio="none"><path d={shade} className="sw-shade" /><line x1="0" x2="560" y1={e.y0} y2={e.y0} className="sw-zero" /><path d={p.d} className="sw-peak" /><path d={e.d} className="sw-ln cyan" /></svg>
      {axis}
    </Card>
  );
}

// --- 5. Streaks --------------------------------------------------------------------------------

export function Streaks({ trades, style }) {
  const { t } = useI18n();
  const d = days(trades);
  const info = 'Green and red trading days in a row: your best run, your worst run, and where you are now.';
  if (!d.length) return <Card title="Streaks" info={info}><Empty text="No trades yet." /></Card>;
  let best = 0;
  let worst = 0;
  let cur = 0;
  let kind = null;
  for (const x of d) {
    const k = x.net > 0 ? 'g' : 'r';
    cur = k === kind ? cur + 1 : 1;
    kind = k;
    if (k === 'g') best = Math.max(best, cur); else worst = Math.max(worst, cur);
  }
  const nowText = t(kind === 'g' ? (cur === 1 ? '{n} green day' : '{n} green days') : (cur === 1 ? '{n} red day' : '{n} red days'), { n: cur });
  const last30 = d.slice(-30);
  const squares = <div className="sw-sq">{last30.map((x) => <i key={x.key} className={x.net > 0 ? 'g' : 'r'} title={`${dayShort(x.key)} ${signed(x.net)}`} />)}</div>;
  if (style === 'b') {
    return (
      <Card title="Current streak" info={info}>
        <div className="sw-row nowrap gap"><span className={`sw-bigring ${kind}`}>{cur}</span><div className="sw-stack"><b>{t(kind === 'g' ? 'Green days in a row' : 'Red days in a row')}</b><span className="sw-muted">{t('Best: {b} green · Worst: {w} red', { b: best, w: worst })}</span></div></div>
        <div className="sw-sq thin">{last30.map((x) => <i key={x.key} className={x.net > 0 ? 'g' : 'r'} />)}</div>
      </Card>
    );
  }
  if (style === 'c') {
    const afterRed = d.slice(1).filter((x, i) => d[i].net < 0);
    const bounce = afterRed.length ? Math.round((afterRed.filter((x) => x.net > 0).length / afterRed.length) * 100) : null;
    return (
      <Card title="Current streak" info={info}>
        <div className={`sw-big ${kind === 'g' ? 'gain' : 'loss'}`}>{nowText}</div>
        <div className="sw-dots">{d.slice(-10).map((x) => <i key={x.key} className={x.net > 0 ? 'g' : 'r'} title={`${dayShort(x.key)} ${signed(x.net)}`} />)}</div>
        <span className="sw-foot">{t('Your last 10 trading days.')}{bounce !== null && ` ${t('After a red day, your next day was green {p}% of the time.', { p: bounce })}`}</span>
      </Card>
    );
  }
  return (
    <Card title="Streaks" info={info}>
      <div className="sw-boxes two"><span><small>{t('Best green run')}</small><b className="gain">{t(best === 1 ? '{n} day' : '{n} days', { n: best })}</b></span><span><small>{t('Worst red run')}</small><b className="loss">{t(worst === 1 ? '{n} day' : '{n} days', { n: worst })}</b></span></div>
      {squares}
      <span className="sw-foot">{t('Now:')} <b className={kind === 'g' ? 'gain' : 'loss'}>{nowText}</b> {t('in a row.')}</span>
    </Card>
  );
}

// --- Account rules widgets ---------------------------------------------------------------------

// The accounts these widgets cover (the picked one, or every shown one), each with its status.
function ruleRows(ctx) {
  const { account, accounts, shownIds, allTrades } = ctx;
  const list = account ? [account] : accounts.filter((a) => !a.archived && (!shownIds || shownIds.includes(a._id)));
  return list.filter((a) => isPropAccount(a) && a.size).map((a) => {
    const mine = allTrades.filter((x) => String(x.accountId) === String(a._id));
    return { account: a, status: accountStatus(a, mine), days: new Set(mine.map((x) => tradingDayKey(x.date))).size };
  }).filter((r) => r.status);
}
function RulesEmpty({ text }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="sw-empty-box"><p>{t(text)}</p><button type="button" className="secondary small" onClick={() => navigate('/app/accounts')}>{t('Set up accounts')}</button></div>
  );
}

// 6. Account progress: where each account sits between its max loss and its target.
export function AccountProgress({ ctx, style }) {
  const { t } = useI18n();
  const rows = ruleRows(ctx).filter((r) => r.status.target && r.status.floor !== undefined);
  const info = 'Where the account sits between its max loss level and its profit target.';
  if (!rows.length) return <Card title="Account progress" info={info}><RulesEmpty text="Add an account with its profit target and max loss to see how far you are from passing." /></Card>;
  return (
    <Card title={style === 'b' ? 'Account risk' : 'Account progress'} info={info}>
      <div className="sw-list">
        {rows.map(({ account, status, days: traded }) => {
          const { balance, floor, target, start } = status;
          const where = Math.max(0, Math.min(100, ((balance - floor) / (target - floor)) * 100));
          const startAt = Math.max(0, Math.min(100, ((start - floor) / (target - floor)) * 100));
          const change = balance - start;
          const risk = status.risk === 'danger' ? ['Near limit', 'r'] : status.risk === 'warning' ? ['Caution', 'y'] : ['Safe', 'g'];
          if (style === 'b') {
            const passPct = Math.max(0, Math.min(100, (change / (target - start)) * 100));
            const room = account.maxLossLimit ? Math.max(0, Math.min(100, (status.toBlow / account.maxLossLimit) * 100)) : null;
            return (
              <div key={account._id} className="sw-arisk">
                <div><small className="sw-muted">{account.name}</small><div className="sw-big">{money(balance)}</div><span className={tone(change)}>{signed(change)}</span></div>
                <div className="sw-stack wide">
                  <span className="sw-muted">{t('To pass')} · <b>{money(Math.max(0, status.toPass))}</b></span><span className="sw-bar big"><i className="c" style={{ width: `${passPct}%` }} /></span>
                  {room !== null && <><span className="sw-muted">{t('Max loss room')} · <b>{money(Math.max(0, status.toBlow))}</b></span><span className="sw-bar big"><i className={room < 25 ? 'r' : room < 50 ? 'y' : 'g'} style={{ width: `${room}%` }} /></span></>}
                </div>
                {status.consistencyPct !== undefined && status.consistencyPct !== null && <Ring pct={(status.consistencyPct / status.consistencyLimit) * 100} kind={status.consistencyOk ? 'g' : 'r'} label={`${Math.round(status.consistencyPct)}%`} sub={t('best day')} />}
              </div>
            );
          }
          if (style === 'c') {
            return (
              <div key={account._id} className="sw-stack">
                <div className="sw-row between"><b>{account.name}</b><span className="sw-big2">{money(balance)}</span></div>
                <div className="sw-zone"><span className="zr" style={{ width: `${startAt}%` }}>{t('max-loss room')}</span><span className="zg" style={{ width: `${100 - startAt}%` }}>{t('profit to target')}</span><span className="sw-pin" style={{ left: `${where}%` }} /></div>
                <div className="sw-row between"><span className="loss">{t('{amount} above max loss', { amount: money(Math.max(0, status.toBlow)) })}</span><span className="gain">{t('{amount} to pass', { amount: money(Math.max(0, status.toPass)) })}</span></div>
              </div>
            );
          }
          return (
            <div key={account._id} className="sw-stack">
              <div className="sw-row between"><div><div className="sw-big">{money(balance)}</div><span className="sw-muted">{account.name} · <span className={tone(change)}>{signed(change)}</span></span></div><Chips items={[[t(risk[0]), risk[1]]]} /></div>
              <div className="sw-track"><span className="sw-pin" style={{ left: `${where}%` }} /><span className="sw-tick" style={{ left: `${startAt}%` }} /></div>
              <div className="sw-row between sw-tiny"><span className="loss">{t('Max loss {n}', { n: money(floor) })}</span><span>{t('Start {n}', { n: money(start) })}</span><span className="gain">{t('Target {n}', { n: money(target) })}</span></div>
              <div className="sw-boxes four">
                <span><small>{t('From max loss')}</small><b className="loss">{money(Math.max(0, status.toBlow))}</b></span>
                <span><small>{t('To pass')}</small><b className="gain">{money(Math.max(0, status.toPass))}</b></span>
                <span><small>{t('Days traded')}</small><b>{traded}</b></span>
                <span><small>{t('Best day')}</small><b>{status.consistencyPct !== undefined && status.consistencyPct !== null ? t('{p}% of profit', { p: Math.round(status.consistencyPct) }) : '—'}</b></span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// 7. Daily loss left: room under today's limit.
export function DailyLoss({ ctx, style }) {
  const { t } = useI18n();
  const rows = ruleRows(ctx).filter((r) => r.status.dailyLeft !== undefined);
  const info = 'How much you can still lose today before your daily loss limit. It resets at the next session.';
  if (!rows.length) return <Card title="Daily loss left" info={info}><RulesEmpty text="Set a daily loss limit on your account to see how much room is left today." /></Card>;
  return (
    <Card title="Daily loss left" info={info} extra={<span className="sw-live">● {t('TODAY')}</span>}>
      <div className="sw-list">
        {rows.map(({ account, status }) => {
          const limit = account.dailyLossLimit;
          const left = Math.max(0, status.dailyLeft);
          const used = Math.round(((limit - left) / limit) * 100);
          const level = status.dailyRisk === 'danger' ? 'Near limit' : status.dailyRisk === 'warning' ? 'Caution' : 'Safe';
          const color = status.dailyRisk === 'danger' ? 'loss' : status.dailyRisk === 'warning' ? 'gold' : 'cyan';
          const name = rows.length > 1 && <b className="sw-acct">{account.name}</b>;
          if (style === 'b') {
            return (
              <div key={account._id} className="sw-stack sw-center">{name}
                <svg className="sw-gauge" viewBox="0 0 140 76" aria-hidden="true"><path d="M18 66 A52 52 0 0 1 122 66" className="gz track" /><path d="M18 66 A52 52 0 0 1 122 66" className={`gz ${status.dailyRisk === 'danger' ? 'r' : status.dailyRisk === 'warning' ? 'y' : 'g'}`} strokeDasharray={`${((100 - used) / 100) * 163.4} 163.4`} /></svg>
                <div className={`sw-big2 ${color}`}>{money(left)}</div><span className="sw-muted">{t('left of {limit} · {p}% used', { limit: money(limit), p: used })}</span>
              </div>
            );
          }
          if (style === 'c') {
            return (
              <div key={account._id} className="sw-stack">{name}
                <div className="sw-row between"><div className={`sw-big2 ${color}`}>{money(left)}</div><span className="sw-muted">{t('{p}% used', { p: used })}</span></div>
                <div className="sw-blocks">{Array.from({ length: 10 }, (_, k) => <i key={k} className={k >= used / 10 ? (status.dailyRisk === 'safe' ? 'g' : status.dailyRisk === 'warning' ? 'y' : 'r') : ''} />)}</div>
                <span className="sw-foot">{t('Each block is {n}. Resets at the next session.', { n: money(limit / 10) })}</span>
              </div>
            );
          }
          return (
            <div key={account._id} className="sw-stack">{name}
              <div className={`sw-big ${color}`}>{money(left)}</div><span className="sw-muted">{t('left today of your {limit} limit', { limit: money(limit) })}</span>
              <div className="sw-grad"><span className="sw-pin" style={{ left: `${used}%` }} /></div>
              <Chips items={[['Safe', 'g'], ['Caution', 'y'], ['Near limit', 'r']].map(([l, k]) => [t(l), l === level ? `${k} on` : ''])} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// 8. Consistency rule: the best day as a share of profit against the limit.
export function Consistency({ ctx, style }) {
  const { t } = useI18n();
  const rows = ruleRows(ctx).filter((r) => r.status.consistencyLimit);
  const info = 'Your best day as a share of your profit. Many firms cap it (often 40–50%), so one big day can’t carry the whole evaluation.';
  if (!rows.length) return <Card title="Consistency rule" info={info}><RulesEmpty text="Add your firm’s consistency rule to your account to check that no single day is too big." /></Card>;
  return (
    <Card title="Consistency rule" info={info}>
      <div className="sw-list">
        {rows.map(({ account, status }) => {
          const pct = status.consistencyPct;
          const lim = status.consistencyLimit;
          const name = rows.length > 1 && <b className="sw-acct">{account.name}</b>;
          if (pct === null || pct === undefined) return <div key={account._id}>{name}<Empty text="No profitable day yet." /></div>;
          const near = status.consistencyOk && pct >= lim * 0.85;
          const badge = !status.consistencyOk ? ['Over limit', 'r'] : near ? ['Watch', 'y'] : ['Within the rule', 'g'];
          const profit = status.change > 0 ? status.change : 0;
          if (style === 'b') {
            return (
              <div key={account._id} className="sw-row nowrap gap">{name}<Ring pct={(pct / Math.max(lim, pct)) * 100} kind={status.consistencyOk ? (near ? 'y' : 'g') : 'r'} label={`${Math.round(pct)}%`} />
                <div className="sw-stack"><b>{t('Best day {n}', { n: money(status.bestDay || 0) })}</b><span className="sw-muted">{t('Rule: {p}% of profit max', { p: lim })}</span><Chips items={[[t(badge[0]), badge[1]]]} /></div></div>
            );
          }
          if (style === 'c') {
            const allowed = (profit * lim) / 100;
            return (
              <div key={account._id} className="sw-stack">{name}
                <div className="sw-cmp">
                  <span><small>{t('Your best day')}</small><b className={status.consistencyOk ? '' : 'loss'}>{money(status.bestDay || 0)}</b><span className="sw-bar"><i className={status.consistencyOk ? 'g' : 'r'} style={{ width: '100%' }} /></span></span>
                  <span><small>{t('Most allowed for one day ({p}%)', { p: lim })}</small><b>{money(allowed)}</b><span className="sw-bar"><i className="m" style={{ width: `${Math.min(100, (allowed / Math.max(1, status.bestDay || 1)) * 100)}%` }} /></span></span>
                </div>
                {!status.consistencyOk && <span className="sw-foot">{t('Your profit needs to reach {n} for this best day to fit the rule.', { n: money((status.bestDay || 0) / (lim / 100)) })}</span>}
              </div>
            );
          }
          return (
            <div key={account._id} className="sw-stack">{name}
              <div className="sw-row between"><div className={`sw-big ${status.consistencyOk ? (near ? 'gold' : 'gain') : 'loss'}`}>{Math.round(pct)}%</div><Chips items={[[t(badge[0]), badge[1]]]} /></div>
              <span className="sw-muted">{t('your best day as a share of profit')}</span>
              <div className="sw-bar big lim"><i className={status.consistencyOk ? (near ? 'y' : 'g') : 'r'} style={{ width: `${Math.min(100, (pct / Math.max(lim, pct)) * 100)}%` }} /><span className="sw-lm" style={{ left: `${(lim / Math.max(lim, pct)) * 100}%` }}><em>{t('{p}% limit', { p: lim })}</em></span></div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// 9. Payout tracker: winning days toward the next payout.
export function Payouts({ ctx, style }) {
  const { t } = useI18n();
  const rows = ruleRows(ctx).filter((r) => r.status.payoutNeeded);
  const info = 'Winning days since your last payout, against the number your firm requires before you can ask for one.';
  if (!rows.length) return <Card title="Payout tracker" info={info}><RulesEmpty text="Add your firm’s payout rule (winning days needed) to your funded account to track when you can request a payout." /></Card>;
  return (
    <Card title="Payout tracker" info={info}>
      <div className="sw-list">
        {rows.map(({ account, status }) => {
          const need = status.payoutNeeded;
          const have = Math.min(status.payoutDays, need);
          const ready = status.payoutDays >= need;
          const paid = status.cash && status.cash.withdrawals ? status.cash.withdrawals : 0;
          const name = rows.length > 1 && <b className="sw-acct">{account.name}</b>;
          if (style === 'a') {
            return (
              <div key={account._id} className="sw-stack">{name}
                <div className="sw-row between"><span className="sw-muted">{t('Qualifying days')}</span><b>{have} / {need}</b></div>
                <div className="sw-pdots" style={{ gridTemplateColumns: `repeat(${need}, 1fr)` }}>{Array.from({ length: need }, (_, k) => <i key={k} className={k < have ? 'g' : ''} />)}</div>
                <div className="sw-boxes two"><span><small>{t('Paid out so far')}</small><b className="gain">{money(paid)}</b></span><span><small>{t('Profit since start')}</small><b className={tone(status.change)}>{signed(status.change)}</b></span></div>
                <span className="sw-foot">{ready ? t('Ready to request a payout.') : t(need - have === 1 ? '1 more qualifying day to unlock your next payout.' : '{n} more qualifying days to unlock your next payout.', { n: need - have })}</span>
              </div>
            );
          }
          return (
            <div key={account._id} className="sw-stack">{name}
              <div className="sw-steps">{Array.from({ length: need }, (_, k) => <span key={k} className={k < have ? 'done' : ''}><i>{k < have ? '✓' : k + 1}</i></span>)}<span className={`goal${ready ? ' ready' : ''}`}><i>$</i></span></div>
              <div className="sw-row between"><span className="sw-muted">{t('{a} of {b} winning days', { a: have, b: need })}</span><b className={ready ? 'gain' : ''}>{ready ? t('Payout ready') : t('{n} to go', { n: need - have })}</b></div>
              {paid > 0 && <span className="sw-foot">{t('{amount} paid out so far', { amount: money(paid) })}</span>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// --- 10. Needs review --------------------------------------------------------------------------

const journaled = (n) => Boolean(n && (n.score || (n.review && n.review.trim()) || (n.eveningDone && n.eveningDone.length)));

export function NeedsReview({ trades, notes = {}, onEditTrade, style }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const waitingList = unjournaledTrades(trades);
  const waiting = waitingList.length;
  const done = trades.length - waiting;
  const info = 'Trades with no tags or notes yet. Reviewing them is where the lessons come from.';
  if (style === 'c') {
    const latest = [...waitingList].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 4);
    return (
      <Card title="Needs review" info={info} extra={waiting > 0 && <button type="button" className="link-button" onClick={() => navigate('/app')}>{t('Review all')} &rarr;</button>}>
        <span className={`sw-big2 ${waiting ? 'gold' : 'gain'}`}>{waiting}</span>
        {latest.length ? (
          <ul className="sw-lst">
            {latest.map((x) => {
              const side = String(x.side || '').toLowerCase().startsWith('s') ? 's' : 'l';
              return <li key={x._id}><button type="button" onClick={() => onEditTrade && onEditTrade(x)}><span><b>{x.symbol}</b> <em className={side}>{t(side === 's' ? 'Short' : 'Long')}</em> <small>{dayShort(tradingDayKey(x.date))}</small></span><b className={tone(netPnl(x))}>{signed(netPnl(x))}</b></button></li>;
            })}
          </ul>
        ) : <Empty text="Every trade has tags or notes. Nice work." />}
      </Card>
    );
  }
  // Journal streak: trading days in a row (newest first) with the day journaled.
  let streak = 0;
  for (const key of Object.keys(notes).sort().reverse()) {
    if (key > currentTradingDay()) continue;
    if (journaled(notes[key])) streak += 1; else if (notes[key] && (notes[key].sessionTrades || []).length) break;
  }
  return (
    <Card title="Trades to review" info={info}>
      <div className="sw-row base"><span className={`sw-big ${waiting ? 'gold' : 'gain'}`}>{waiting}</span><span className="sw-muted">{t('of {n} trades', { n: trades.length })}</span></div>
      {trades.length > 0 && <div className="sw-wl"><b style={{ flex: done || 0.02 }} /><i className="y" style={{ flex: waiting || 0.02 }} /></div>}
      <div className="sw-row between sw-tiny"><span className="gain">{t('{n} reviewed', { n: done })}</span><span className="gold">{t('{n} waiting', { n: waiting })}</span></div>
      <div className="sw-row between"><span className="sw-muted">🔥 {t(streak === 1 ? 'Journal streak {n} day' : 'Journal streak {n} days', { n: streak })}</span>{waiting > 0 && <button type="button" className="link-button" onClick={() => navigate('/app')}>{t('Review')} &rarr;</button>}</div>
      {waiting > 5 && <span className="sw-foot">{t('5 a day clears it in {n} days.', { n: Math.ceil(waiting / 5) })}</span>}
    </Card>
  );
}

// --- 11. P&L by time of day --------------------------------------------------------------------

export function TimeOfDay({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('all');
  const map = new Map();
  for (const x of since(trades, period)) {
    if (x.timeUnknown) continue;
    const h = Number(NY_HOUR.format(new Date(x.date)));
    map.set(h, (map.get(h) || 0) + netPnl(x));
  }
  const hours = [...map.entries()].sort((a, b) => a[0] - b[0]);
  const seg = <Seg options={[['30d', '30D'], ['all', 'All']]} value={period} onChange={setPeriod} />;
  const info = 'Your P&L by the hour each trade opened, New York time. Green hours make money; red hours cost you.';
  if (!hours.length) return <Card title="P&L by time of day" info={info} extra={seg}><Empty text="No trades with a time yet." /></Card>;
  const max = Math.max(1, ...hours.map(([, v]) => Math.abs(v)));
  const best = hours.reduce((b, h) => (h[1] > b[1] ? h : b));
  const worst = hours.reduce((b, h) => (h[1] < b[1] ? h : b));
  const chips = <Chips items={[best[1] > 0 && [t('Best {h} · {n}', { h: hourLabel(best[0]), n: signed(best[1]) }), 'g'], worst[1] < 0 && [t('Worst {h} · {n}', { h: hourLabel(worst[0]), n: signed(worst[1]) }), 'r']]} />;
  if (style === 'b') {
    return (
      <Card title="P&L by time of day" info={info} extra={seg}>
        {chips}
        <ul className="sw-hsplit">{hours.map(([h, v]) => <li key={h}><span>{hourLabel(h)}</span><span className="tk"><i className={tone(v)} style={{ width: `${(Math.abs(v) / max) * 50}%`, [v < 0 ? 'right' : 'left']: '50%' }} /></span><b className={tone(v)}>{signed(v)}</b></li>)}</ul>
      </Card>
    );
  }
  if (style === 'c') {
    return (
      <Card title="P&L by time of day" info={info} extra={seg}>
        <div className="sw-heat">{hours.map(([h, v]) => <span key={h} style={{ background: `rgba(${v >= 0 ? '47,227,154' : '255,93,115'},${(0.14 + 0.8 * (Math.abs(v) / max)).toFixed(2)})` }} title={`${hourLabel(h)} ${signed(v)}`}><b>{hourLabel(h).replace(' ', '')}</b><small>{shortMoney(v)}</small></span>)}</div>
        <span className="sw-foot">{t('Darker green means more profit that hour, darker red more loss.')}</span>
      </Card>
    );
  }
  return (
    <Card title="P&L by time of day" info={info} extra={seg}>
      {chips}
      <div className="sw-vbars">{hours.map(([h, v]) => (
        <span key={h} className="vb" title={`${hourLabel(h)} ${signed(v)}`}>
          <span className="up">{v > 0 && <i className="g" style={{ height: `${(v / max) * 100}%` }} />}</span>
          <span className="dn">{v < 0 && <i className="r" style={{ height: `${(-v / max) * 100}%` }} />}</span>
          <small>{hourLabel(h).replace(' ', '')}</small>
        </span>
      ))}</div>
      {best[1] > 0 && worst[1] < 0 && <span className="sw-foot">{t('Your best hour is {b}; {w} costs you the most.', { b: hourLabel(best[0]), w: hourLabel(worst[0]) })}</span>}
    </Card>
  );
}

// The right widget for an id, in its style.
export function StyledWidget({ id, style, ctx }) {
  const { trades, notes, onEditTrade } = ctx;
  switch (id) {
    case 'net': return <NetPnl trades={trades} style={style} />;
    case 'winRate': return <WinRate trades={trades} style={style} />;
    case 'profitFactor': return <ProfitFactor trades={trades} style={style} />;
    case 'equity': return <EquityDrawdown trades={trades} style={style} />;
    case 'streak': return <Streaks trades={trades} style={style} />;
    case 'propProgress': return <AccountProgress ctx={ctx} style={style} />;
    case 'dailyLoss': return <DailyLoss ctx={ctx} style={style} />;
    case 'consistency': return <Consistency ctx={ctx} style={style} />;
    case 'payouts': return <Payouts ctx={ctx} style={style} />;
    case 'needsReview': return <NeedsReview trades={trades} notes={notes} onEditTrade={onEditTrade} style={style} />;
    case 'byHour': return <TimeOfDay trades={trades} style={style} />;
    default: return NEW_STYLES[id] ? <NewWidget id={id} style={style} ctx={ctx} /> : null;
  }
}
